import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GoldDots } from "@/components/Brand";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { errMsg, formatDate, useApi } from "@/lib/api";
import { Panel, Stat } from "@/components/admin/AdminUI";

const CountryEmail = ({ countries }) => {
  const api = useApi();
  const [f, setF] = useState({ country: "", subject: "AHU membership is now open where you are", message: "" });
  const ready = f.country && f.subject.length >= 3 && f.message.length >= 10;
  const send = async () => {
    try { const { data } = await api.post("/admin/overseas/email", f); toast.success(`Sending to ${data.sending} ${data.sending === 1 ? "person" : "people"}`); setF({ ...f, message: "" }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Email a country" sub="When membership opens in a country, email everyone on the list there. Each email has an unsubscribe link." testId="overseas-email">
      <div className="space-y-4">
        <select className="field appearance-none" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} data-testid="overseas-email-country">
          <option value="" disabled>Choose a country</option>
          {countries.map(([c, n]) => <option key={c} value={c}>{c} ({n})</option>)}
        </select>
        <input className="field" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="overseas-email-subject" />
        <textarea className="field" rows={6} placeholder="Your message. Leave a blank line between paragraphs. It starts with Kia ora and their name, and ends with your sign-off."
          value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="overseas-email-message" />
        {ready && (
          <ConfirmDialog title={`Email everyone in ${f.country}?`} body="This sends your message to every subscribed person on the list for this country." confirmLabel="Send"
            onConfirm={send} testId="overseas-email-confirm" trigger={<button className="btn-charcoal" data-testid="overseas-email-send-btn">Send email</button>} />
        )}
      </div>
    </Panel>
  );
};

export const AdminOverseas = () => {
  const api = useApi();
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/overseas").then((r) => setD(r.data));
  useEffect(() => { load(); }, [api]); // eslint-disable-line
  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/overseas/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement("a"), { href: url, download: "ahu-let-me-know.csv" }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const review = async (sid, reviewed) => { try { await api.patch(`/admin/overseas/payments/${sid}`, { reviewed }); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!d) return <div className="py-16 flex justify-center"><GoldDots /></div>;
  const listCountries = Object.entries(d.interest.filter((r) => !r.unsubscribed).reduce((a, r) => ({ ...a, [r.country]: (a[r.country] || 0) + 1 }), {}));
  return (
    <div className="space-y-5">
      <Panel title="People outside New Zealand" testId="overseas-users">
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4 mb-4">
          <Stat n={d.users_outside_nz} label="Accounts outside NZ" testId="overseas-count" />
          <Stat n={d.interest.filter((r) => !r.unsubscribed).length} label="On the Let me know list" testId="interest-count" />
        </div>
        {d.by_country.length ? <ul className="divide-y divide-rule" data-testid="overseas-by-country">{d.by_country.map(([c, n]) => (
          <li key={c} className="py-2.5 flex justify-between text-[15px]"><span>{c}</span><span className="text-ink-soft">{n}</span></li>))}</ul>
          : <p className="text-ink-soft">No accounts outside New Zealand yet.</p>}
      </Panel>
      <Panel title="Let me know list" sub="From the membership screen (Account) and app.ahucoach.com/notify (No account)." testId="interest-list"
        right={<button className="btn-outline !min-h-[40px] !py-2 text-sm" onClick={exportCsv} data-testid="interest-export-btn">Export CSV</button>}>
        {d.interest.length === 0 ? <p className="text-ink-soft">Nobody yet.</p> : (
          <ul className="divide-y divide-rule">{d.interest.map((r) => (
            <li key={r.id} className="py-3 grid gap-1 sm:grid-cols-[1fr_140px_110px_90px] sm:gap-4 text-sm" data-testid={`interest-${r.id}`}>
              <span className="min-w-0 break-words">{r.unsubscribed ? <span className="text-ink-soft">Unsubscribed, details deleted</span> : <>{r.preferred_name} · <span className="text-ink-soft">{r.email}</span></>}</span>
              <span>{r.country}</span>
              <span className="text-ink-soft">{formatDate(r.created_at)}</span>
              <span className="text-ink-soft">{r.source === "account" ? "Account" : "No account"}</span>
            </li>))}</ul>
        )}
      </Panel>
      <CountryEmail countries={listCountries} />
      <Panel title="Payments from outside New Zealand" sub="Memberships paid with a billing country other than New Zealand. Review and refund in Stripe if needed." testId="overseas-payments">
        {d.flagged_payments.length === 0 ? <p className="text-ink-soft" data-testid="no-flagged-payments">None so far.</p> : (
          <ul className="divide-y divide-rule">{d.flagged_payments.map((f) => (
            <li key={f.session_id} className="py-3 flex flex-wrap items-center justify-between gap-3 text-sm" data-testid={`flagged-${f.session_id}`}>
              <span>{f.email || "Closed account"} · {f.billing_country} · ${f.amount} {f.currency?.toUpperCase()} · {formatDate(f.updated_at)}</span>
              <button className="btn-quiet text-sm" onClick={() => review(f.session_id, !f.reviewed)} data-testid={`flag-review-${f.session_id}`}>{f.reviewed ? "Reviewed" : "Mark reviewed"}</button>
            </li>))}</ul>
        )}
      </Panel>
    </div>
  );
};
